import React, { useRef } from 'react'

import { Box, Text, Link } from '@clear/design-system'
import { EnrollmentLocation } from '@utils/getNearbyEnrollmentLocations'
import { LaneInfo } from './LaneInfo'
import { ConversionCouponInfo } from './ConversionCouponInfo'
import { useFlag } from '@hooks/useFlag'
import { AccessibleLocation } from './AccessibleLocation'

export interface ILocationInfo {
  location: EnrollmentLocation
  index?: number
}

const V1LocationInfo = ({ location, displayConversionCouponInfo }) => {
  return (
    <>
      <Box>
        <Text data-testid="v1-location-name" as="h5" fontSize={4} mb={2}>
          {location?.name}
        </Text>
        <Text
          data-testid="v1-location-distance-and-address"
          fontSize={0}
          color="neutralsTextGray"
          display="inline-block"
        >
          {location?.distance} miles away •{' '}
          <Link
            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURI(
              location.address
            )}`}
            target="_blank"
          >
            {location?.address}
          </Link>
        </Text>
        <br />
        <Text
          data-testid="v1-location-important-info"
          fontSize={0}
          color="utilityDarkErrorRed"
        >
          {location?.importantInfo}
        </Text>
        <Text
          className="flex flex-col"
          fontSize={0}
          color="neutralsPlaceholderGray"
        >
          {location?.lanes.map(
            (
              {
                name,
                description,
                isAccessible,
                availability,
                accessibilityMessage,
                importantInfo,
                id,
              },
              i
            ) => (
              <LaneInfo
                key={id}
                id={id}
                name={name}
                description={description}
                openDaily={location?.openDaily}
                accessibilityMessage={accessibilityMessage}
                isAccessible={isAccessible}
                availability={availability}
                conversionCouponInfo={location?.conversionCouponInfo || ''}
                importantInfo={importantInfo}
                lastLane={location?.lanes.length - 1 === i}
              />
            )
          )}
          {(!location?.lanes || location?.lanes?.length === 0) &&
          location.conversionCouponInfo &&
          displayConversionCouponInfo ? (
            <ConversionCouponInfo
              conversionCouponInfo={location.conversionCouponInfo}
            />
          ) : null}
        </Text>
      </Box>
    </>
  )
}

const V2LocationInfo = ({ location, displayConversionCouponInfo, index }) => {
  const isLocationAccessibleRef = useRef(false)
  const accessibilityMessageRef = useRef('')
  return (
    <>
      <Box
        style={{
          display: 'grid',
          gap: '0.375rem',
        }}
      >
        <Text
          data-testid="v2-location-name"
          as="h5"
          fontSize={4}
          fontWeight={500}
          ml={1}
        >
          {index}. {location?.name}
        </Text>
        <Text
          data-testid="v2-location-distance"
          fontSize={3}
          color="neutralsTextGray"
          display="inline-block"
          fontWeight={600}
        >
          {location?.distance} miles
        </Text>
        {location?.importantInfo && (
          <Text
            data-testid="v2-location-important-info"
            fontSize={0}
            color="utilityDarkErrorRed"
          >
            {location?.importantInfo}
          </Text>
        )}
        <Box
          className="flex flex-col"
          fontSize={0}
          color="neutralsPlaceholderGray"
          style={{
            display: 'grid',
            gap: '0.375rem',
          }}
        >
          {location?.lanes.map(
            (
              {
                name,
                description,
                isAccessible,
                availability,
                accessibilityMessage,
                importantInfo,
                id,
              },
              i: number
            ) => {
              if (!isLocationAccessibleRef.current) {
                isLocationAccessibleRef.current = true
              }
              if (!accessibilityMessageRef.current) {
                accessibilityMessageRef.current = accessibilityMessage
              }
              return (
                <LaneInfo
                  key={id}
                  id={id}
                  name={name}
                  description={description}
                  openDaily={location?.openDaily}
                  accessibilityMessage={accessibilityMessage}
                  isAccessible={isAccessible}
                  availability={availability}
                  conversionCouponInfo={location?.conversionCouponInfo || ''}
                  importantInfo={importantInfo}
                  lastLane={location?.lanes.length - 1 === i}
                />
              )
            }
          )}
          <Text
            data-testid="v2-location-address"
            fontSize={1}
            fontWeight={400}
            color="neutralsTextGray"
            display="inline-block"
          >
            {location?.address}
          </Text>
          {(!location?.lanes || location?.lanes?.length === 0) &&
          location.conversionCouponInfo &&
          displayConversionCouponInfo ? (
            <ConversionCouponInfo
              conversionCouponInfo={location.conversionCouponInfo}
            />
          ) : null}
          <AccessibleLocation
            isAccessible={isLocationAccessibleRef.current}
            accessibilityMessage={accessibilityMessageRef.current}
          />
        </Box>
      </Box>
    </>
  )
}

export const LocationInfo = ({ location, index }: ILocationInfo) => {
  const displayConversionCouponInfo =
    useFlag('locations')?.displayConversionCouponInfo
  const displayLocationSearchRevisedLayout =
    useFlag('layoutRefresh')?.displayLocationSearchRevisedLayout
  if (displayLocationSearchRevisedLayout) {
    return (
      <V2LocationInfo
        index={index}
        location={location}
        displayConversionCouponInfo={displayConversionCouponInfo}
      />
    )
  } else
    return (
      <V1LocationInfo
        location={location}
        displayConversionCouponInfo={displayConversionCouponInfo}
      />
    )
}
